// rbac.ts — roles, people and capability map, ported from the prototype's data.jsx.
// In production the active role comes from the authenticated user's Entra claims;
// the role switcher (Topbar) remains for demo/QA until claims are wired.
// Now also includes a backend→frontend role code mapping for /api/identity/me/.

export type RoleId =
  | 'inventory_manager'
  | 'pharmacist'
  | 'store_assistant'
  | 'dispensing_technician'
  | 'compliance_officer'
  | 'auditor'
  | 'system_admin'
  | 'regional_manager';

/**
 * Maps backend role codes (from UserRole / UserStoreAssignment) to frontend RoleId.
 * The backend stores uppercase short codes; the frontend uses descriptive slugs.
 */
export const BACKEND_ROLE_MAP: Record<string, RoleId> = {
  ADM: 'system_admin',
  SY: 'system_admin',
  IM: 'inventory_manager',
  PH: 'pharmacist',
  SA: 'store_assistant',
  DT: 'dispensing_technician',
  CO: 'compliance_officer',
  AUD: 'auditor',
  AU: 'auditor',
  RM: 'regional_manager',
};

/** Pick the highest-privilege frontend role from a list of backend codes. */
export function bestRoleFromCodes(codes: string[]): RoleId {
  // Privilege order (most → least). system_admin must rank first so a multi-role
  // user (e.g. ADM+IM) resolves to system_admin and keeps settings.edit (MD-8).
  const priority: RoleId[] = [
    'system_admin',
    'regional_manager',
    'inventory_manager',
    'pharmacist',
    'dispensing_technician',
    'store_assistant',
    'compliance_officer',
    'auditor',
  ];
  const mapped = codes.map((c) => BACKEND_ROLE_MAP[c.toUpperCase()]).filter(Boolean);
  for (const r of priority) {
    if (mapped.includes(r)) return r;
  }
  return mapped[0] ?? 'store_assistant';
}

/** Map ALL backend codes to their frontend roles (deduped). The user's effective
 *  capabilities are the UNION across these — a pharmacist who is also an admin
 *  keeps the operational pharmacist caps (clinical check, pick, dispense) that
 *  the single "best" role (system_admin) lacks. */
export function rolesFromCodes(codes: string[]): RoleId[] {
  const mapped = codes.map((c) => BACKEND_ROLE_MAP[c.toUpperCase()]).filter(Boolean) as RoleId[];
  return [...new Set(mapped)];
}

// Roles that span EVERY store in the tenant — the mirror of the backend's
// ALL_STORE_ROLE_CODES = {ADM, SY, IM}. Only these may use the group-wide "All Stores" view;
// every other role is store-scoped and must pick from its assigned stores.
const ALL_STORE_ROLES: RoleId[] = ['system_admin', 'inventory_manager'];

export function hasAllStoreAccess(roles: RoleId[]): boolean {
  return roles.some((r) => ALL_STORE_ROLES.includes(r));
}

export interface Role {
  id: RoleId;
  name: string;
  short: string;
  desc: string;
  readOnly: boolean;
}

export const ROLES: Record<RoleId, Role> = {
  inventory_manager: {
    id: 'inventory_manager',
    name: 'Inventory Manager',
    short: 'IM',
    desc: 'Full CRUD on products, batches & bins; approves adjustments and reconciliations.',
    readOnly: false,
  },
  pharmacist: {
    id: 'pharmacist',
    name: 'Pharmacist',
    short: 'Ph',
    desc: 'Read + FEFO override; approves controlled flows; manages expiry holds.',
    readOnly: false,
  },
  store_assistant: {
    id: 'store_assistant',
    name: 'Store Assistant',
    short: 'SA',
    desc: 'Read + draft adjustments; performs counts. No approvals.',
    readOnly: false,
  },
  dispensing_technician: {
    id: 'dispensing_technician',
    name: 'Dispensing Technician',
    short: 'DT',
    desc: 'Builds drafts, scans, assigns baskets & part-packs. No posting or approvals.',
    readOnly: false,
  },
  compliance_officer: {
    id: 'compliance_officer',
    name: 'Compliance Officer',
    short: 'CO',
    desc: 'Read-only with full audit-trail access.',
    readOnly: true,
  },
  auditor: {
    id: 'auditor',
    name: 'Auditor',
    short: 'AU',
    desc: 'Read-only across all modules; reports & exports only.',
    readOnly: true,
  },
  system_admin: {
    id: 'system_admin',
    name: 'System Administrator',
    short: 'SY',
    desc: 'Configures categories, bin types, thresholds, FEFO rules & permissions.',
    readOnly: false,
  },
  regional_manager: {
    id: 'regional_manager',
    name: 'Regional Manager',
    short: 'RM',
    desc: 'Network-wide visibility across their region; oversight and approval of transfers.',
    readOnly: false,
  },
};

export const PEOPLE: Record<RoleId, string> = {
  inventory_manager: 'Alexander Reid',
  pharmacist: 'Sarah Johnson',
  store_assistant: 'Daniel Okafor',
  dispensing_technician: 'Olivia Grant',
  compliance_officer: 'Priya Nair',
  auditor: 'Thomas Webb',
  system_admin: 'Marcus Bell',
  regional_manager: 'Nadia Hussain',
};

// capability → roles allowed
export const CAPS: Record<string, RoleId[]> = {
  'product.create': ['inventory_manager', 'system_admin'],
  'product.edit': ['inventory_manager', 'system_admin'],
  'product.archive': ['inventory_manager', 'system_admin'],
  'batch.add': ['inventory_manager', 'system_admin'],
  'batch.move': ['inventory_manager', 'pharmacist', 'system_admin'],
  'batch.hold': ['inventory_manager', 'pharmacist', 'system_admin'],
  // Blocked → Free Hold is a pharmacist decision (backend inventory.release = PH/ADM).
  'batch.release': ['pharmacist', 'system_admin'],
  'batch.dispose': ['inventory_manager', 'pharmacist', 'system_admin'],
  'bin.create': ['inventory_manager', 'system_admin'],
  'bin.edit': ['inventory_manager', 'system_admin'],
  'bin.assign': ['inventory_manager', 'system_admin'],
  'adjustment.draft': [
    'inventory_manager',
    'pharmacist',
    'store_assistant',
    'dispensing_technician',
    'system_admin',
  ],
  'adjustment.approve': ['inventory_manager', 'pharmacist', 'system_admin'],
  'reconciliation.start': ['inventory_manager', 'store_assistant', 'system_admin'],
  'reconciliation.count': [
    'inventory_manager',
    'pharmacist',
    'store_assistant',
    'dispensing_technician',
    'system_admin',
  ],
  'reconciliation.approve': ['inventory_manager', 'system_admin'],
  'alert.acknowledge': [
    'inventory_manager',
    'pharmacist',
    'store_assistant',
    'dispensing_technician',
    'system_admin',
  ],
  'alert.resolve': ['inventory_manager', 'pharmacist', 'system_admin'],
  'fefo.override': ['pharmacist', 'inventory_manager', 'system_admin'],
  'grn.submit': [
    'inventory_manager',
    'pharmacist',
    'store_assistant',
    'dispensing_technician',
    'system_admin',
  ],
  'dispense.confirm': [
    'inventory_manager',
    'pharmacist',
    'store_assistant',
    'dispensing_technician',
    'system_admin',
  ],
  'import.run': ['inventory_manager', 'system_admin'],
  'settings.edit': ['system_admin'],
  // Audit & Compliance module access (sidebar lock + /compliance route guard).
  // Mirrors the backend's audit.read holders (identity ROLE_PERMISSIONS:
  // ADM/SY, AUD, CO, RM) — keep in sync when either side changes.
  'audit.read': ['system_admin', 'auditor', 'compliance_officer', 'regional_manager'],
  // Master Data module access (referenced by the sidebar lock).
  'master.access': ['inventory_manager', 'system_admin'],
  'master.edit': ['system_admin', 'inventory_manager'],
  'master.policy': ['system_admin', 'inventory_manager'],
  // STOCKS module — page-level view access
  'stk.view': [
    'inventory_manager',
    'pharmacist',
    'store_assistant',
    'dispensing_technician',
    'compliance_officer',
    'auditor',
    'system_admin',
    'regional_manager',
  ],
  // Direct Import (quick local stock entry) — stage & commit stock straight to inventory,
  // deliberately separate from the inward (GRN) flow. Same privilege level as posting inward.
  'stk.direct_import.add': ['inventory_manager', 'pharmacist', 'system_admin'],
  // STOCKS module (SysAdmin has no stock actions; CO/Auditor are read-only).
  'stk.inward.create': [
    'inventory_manager',
    'pharmacist',
    'store_assistant',
    'dispensing_technician',
    'system_admin',
  ],
  'stk.inward.approve': ['inventory_manager', 'pharmacist', 'system_admin'],
  'stk.inward.post': ['inventory_manager', 'pharmacist', 'system_admin'],
  'stk.inward.void': ['inventory_manager', 'pharmacist', 'system_admin'],
  'stk.dispense.create': [
    'inventory_manager',
    'pharmacist',
    'store_assistant',
    'dispensing_technician',
    'system_admin',
  ],
  'stk.dispense.post': ['inventory_manager', 'pharmacist', 'system_admin'],
  'stk.fefo.override': ['pharmacist', 'inventory_manager', 'system_admin'],
  'stk.cd.witness': ['pharmacist', 'inventory_manager', 'system_admin'],
  'stk.tray.scan': [
    'inventory_manager',
    'pharmacist',
    'store_assistant',
    'dispensing_technician',
    'system_admin',
  ],
  // v1.1 static-tray pool — assign/release (PRESCRIPTION_DISPENSING_PLAN App. A)
  'stk.tray.assign': ['pharmacist', 'store_assistant', 'inventory_manager', 'system_admin'],
  'stk.tray.override': ['pharmacist', 'inventory_manager', 'system_admin'],
  'stk.partpack.use': [
    'inventory_manager',
    'pharmacist',
    'store_assistant',
    'dispensing_technician',
    'system_admin',
  ],
  'stk.transfer.create': [
    'inventory_manager',
    'pharmacist',
    'store_assistant',
    'dispensing_technician',
    'system_admin',
  ],
  'stk.transfer.post': ['inventory_manager', 'pharmacist', 'system_admin'],
  'stk.adjustment.draft': [
    'inventory_manager',
    'pharmacist',
    'store_assistant',
    'dispensing_technician',
    'system_admin',
  ],
  'stk.adjustment.approve': ['inventory_manager', 'pharmacist', 'system_admin'],
  'stk.report.run': [
    'inventory_manager',
    'pharmacist',
    'compliance_officer',
    'auditor',
    'system_admin',
  ],
  'stk.settings.edit': ['system_admin'],
  // PRESCRIPTIONS module (PH = clinical authority; SA/DT prep only; CO/Auditor read-only).
  'rx.view': [
    'pharmacist',
    'inventory_manager',
    'store_assistant',
    'dispensing_technician',
    'compliance_officer',
    'system_admin',
    'auditor',
  ],
  'rx.fetch': [
    'pharmacist',
    'store_assistant',
    'dispensing_technician',
    'inventory_manager',
    'system_admin',
  ],
  'rx.prep': ['pharmacist', 'store_assistant', 'dispensing_technician', 'system_admin'],
  'rx.allocate': ['pharmacist', 'inventory_manager', 'system_admin'],
  // Phase-1 clinical-check gate — pharmacist + system_admin (PRESCRIPTION_DISPENSING_PLAN App. A)
  'rx.clinical_check': ['pharmacist', 'system_admin'],
  'rx.scan': ['pharmacist', 'store_assistant', 'dispensing_technician', 'system_admin'],
  // manual product selection on red scan / batch override / expiry override
  'rx.override': ['pharmacist', 'system_admin'],
  'rx.dispense': ['pharmacist', 'system_admin'],
  // D1: cancel a script before it is dispensed (backend prescription.cancel)
  'rx.cancel': ['pharmacist', 'system_admin'],
  // M3-D2: reveal one script's full NHS number, audited (backend patient-search roles PH/ADM)
  'rx.nhs.reveal': ['pharmacist', 'system_admin'],
  'rx.cd.auth': ['pharmacist'],
  'rx.status': ['pharmacist', 'inventory_manager', 'system_admin'],
  'rx.manual': ['pharmacist', 'store_assistant', 'dispensing_technician', 'system_admin'],
  'rx.token.print': ['pharmacist', 'store_assistant', 'dispensing_technician', 'system_admin'],
  'rx.nhs.config': ['system_admin'],
  // MULTI-STORE module (RM = network oversight; SA/DT request/claim only).
  'mst.view': [
    'regional_manager',
    'inventory_manager',
    'pharmacist',
    'store_assistant',
    'dispensing_technician',
    'compliance_officer',
    'system_admin',
    'auditor',
  ],
  'mst.request': [
    'regional_manager',
    'inventory_manager',
    'pharmacist',
    'store_assistant',
    'dispensing_technician',
  ],
  'mst.claim': [
    'regional_manager',
    'inventory_manager',
    'pharmacist',
    'store_assistant',
    'dispensing_technician',
  ],
  'mst.approve': ['regional_manager', 'inventory_manager'],
  'mst.urgent': ['regional_manager', 'inventory_manager', 'pharmacist'],
  'mst.amend': [
    'regional_manager',
    'inventory_manager',
    'pharmacist',
    'store_assistant',
    'dispensing_technician',
  ],
  'mst.discrepancy': ['regional_manager', 'inventory_manager'],
  'mst.report': ['regional_manager', 'inventory_manager', 'compliance_officer', 'auditor'],
  'mst.settings': ['system_admin'],
};

export function can(role: RoleId, cap: string): boolean {
  return (CAPS[cap] || []).includes(role);
}
